'use client'
import { cn } from '@/utilities/ui'
import React from 'react'
import { ChevronRight, ChevronLeft } from 'lucide-react'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  className?: string
  loading?: boolean
}

const MAX_VISIBLE_PAGES = 7

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  className,
  loading = false,
}) => {
  // Calculate which page numbers to show
  const getPageNumbers = () => {
    // If we have 7 or fewer pages, show all of them
    if (totalPages <= MAX_VISIBLE_PAGES) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    // Always show first and last page, and some pages around the current page
    const pageNumbers: number[] = []

    // Add page 1
    pageNumbers.push(1)

    // Add pages around current page
    const startPage = Math.max(2, currentPage - 2)
    const endPage = Math.min(totalPages - 1, currentPage + 2)

    // Add ellipsis after page 1 if needed
    if (startPage > 2) {
      pageNumbers.push(-1) // -1 indicates ellipsis
    }

    // Add the range of pages
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i)
    }

    // Add ellipsis before last page if needed
    if (endPage < totalPages - 1) {
      pageNumbers.push(-2) // -2 indicates ellipsis
    }

    // Add last page
    if (totalPages > 1) {
      pageNumbers.push(totalPages)
    }

    return pageNumbers
  }

  const pageNumbers = getPageNumbers()

  return (
    <div className={cn('flex items-center justify-center space-x-1 py-8', className)}>
      <div className="flex items-center space-x-4">
        {/* Previous page button */}
        {currentPage > 1 && (
          <button
            onClick={() => !loading && onPageChange(currentPage - 1)}
            className={cn('text-muted-foreground cursor-pointer transition-colors', {
              'opacity-50 cursor-not-allowed': loading,
            })}
            disabled={loading}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}

        {pageNumbers.map((pageNumber, index) => {
          // Handle ellipsis
          if (pageNumber < 0) {
            return (
              <span key={`ellipsis-${index}`} className="text-muted-foreground px-1">
                ...
              </span>
            )
          }

          const isCurrentPage = pageNumber === currentPage

          return (
            <button
              key={pageNumber}
              onClick={() => !loading && onPageChange(pageNumber)}
              className={cn('relative text-lg font-medium px-3 py-2 transition-colors', {
                'text-primary cursor-default': isCurrentPage,
                'text-muted-foreground hover:text-foreground cursor-pointer': !isCurrentPage,
                'opacity-50 cursor-not-allowed': loading,
              })}
              disabled={loading || isCurrentPage}
            >
              {pageNumber}
              {isCurrentPage && (
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-primary"></div>
              )}
            </button>
          )
        })}

        {/* Next page button */}
        {currentPage < totalPages && (
          <button
            onClick={() => !loading && onPageChange(currentPage + 1)}
            className={cn('text-muted-foreground cursor-pointer transition-colors', {
              'opacity-50 cursor-not-allowed': loading,
            })}
            disabled={loading}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}

export default Pagination
